import React, {useCallback, useRef} from 'react';
import {Editor, Transforms, Node, Text} from 'slate';
import {Editable, ReactEditor} from 'slate-react';

import SelectionPopupMenu from '../../components/Toolbar/SelectionPopupMenu';

import {handlePrint} from './constants/CommonFunctions';
import {useKeyboardShortcuts} from './hooks/useKeyboardShortcuts';
import {SaveSource} from './hooks/useReportDocumentSave';
import {
  CommentGroupOverlay,
  ElementRenderer,
  FloatingCommentButton,
  LeafRenderer
} from './renderers';
import {sanitizeRange} from './utils/selectionHelpers';

const EditorPage: React.FC<{
  editor: Editor;
  toggleMark: (format: string) => void;
  toggleBlock: (format: string) => void;
  setAlignment: (align: string) => void;
  setFontSize: (size: number) => void;
  setFontFamily: (font: string) => void;
  toggleColor: (type: string, color: string) => void;
  insertLink: () => void;
  insertComment: () => void;
  toggleCommentPanel?: () => void;
  handleToggleList: (format: string) => void;
  saveDocument: (source?: SaveSource) => Promise<void>;
  clearSelections: () => void;
  decorate: any;
  toolbarSync: any;
  openColorPicker: (
    type: 'text' | 'highlight' | 'block-bg',
    anchorEl?: HTMLElement | null
  ) => void;
  indentList: (editor: Editor) => void;
  outdentList: (editor: Editor) => void;
  isHeaderFooterEditMode: boolean;
  setIsHeaderFooterEditMode: (val: boolean) => void;
  isPathInsideHeaderFooter: (path: number[]) => boolean;
}> = ({
  editor,
  toggleMark,
  toggleBlock,
  setAlignment,
  setFontSize,
  setFontFamily,
  toggleColor,
  insertLink,
  insertComment,
  toggleCommentPanel,
  handleToggleList,
  saveDocument,
  clearSelections,
  decorate,
  toolbarSync,
  openColorPicker,
  indentList,
  outdentList,
  isHeaderFooterEditMode,
  setIsHeaderFooterEditMode,
  isPathInsideHeaderFooter
}) => {
  const editorPanelRef = useRef<HTMLDivElement>(null);

  const handleEditorDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/framework-question');
    if (!data) return;
    try {
      const item = JSON.parse(data);
      Transforms.insertNodes(editor, [
        {
          type: 'framework-question',
          key: item.key,
          question: item.question,
          framework: item.framework,
          year: item.year,
          width: 400,
          height: 150,
          x: 0,
          y: 0,
          lockAspectRatio: false,
          children: [{text: ''}]
        },
        {type: 'paragraph', children: [{text: ''}]}
      ]);
    } catch (e) {}
  };

  const moveCursorToFirstBodyTextOrDeselect = useCallback(() => {
    try {
      for (let p = 0; p < editor.children.length; p++) {
        const page = Node.get(editor, [p]) as any;
        if (!page?.children) continue;
        for (let c = 0; c < page.children.length; c++) {
          const child = page.children[c];
          if (
            child.type === 'header-wrapper' ||
            child.type === 'footer-wrapper'
          )
            continue;
          const start = Editor.start(editor, [p, c]);
          if (start) {
            Transforms.select(editor, {anchor: start, focus: start});

            return true;
          }
        }
      }
    } catch (e) {}
    Transforms.deselect(editor);

    return false;
  }, [editor]);
  const handleEditableMouseDownCapture = useCallback(
    (event: React.MouseEvent) => {
      const el = event.target as HTMLElement | null;
      const isHF = el?.closest?.('[data-header-footer="true"]');
      if (isHF) {
        if (event.detail >= 2) {
          setIsHeaderFooterEditMode(true);
          try {
            const r = ReactEditor.findEventRange(editor, event.nativeEvent);
            if (r) Transforms.select(editor, r);
          } catch (e) {}

          return;
        }
        if (!isHeaderFooterEditMode) {
          event.preventDefault();
          event.stopPropagation();
        }

        return;
      }
      if (isHeaderFooterEditMode) {
        setIsHeaderFooterEditMode(false);
        try {
          const r = ReactEditor.findEventRange(editor, event.nativeEvent);
          const safe = sanitizeRange(r, editor);
          if (safe && !isPathInsideHeaderFooter(safe.anchor.path)) {
            Transforms.select(editor, {
              anchor: safe.anchor,
              focus: safe.anchor
            });

            return;
          }
        } catch (e) {}
        moveCursorToFirstBodyTextOrDeselect();
      }
    },
    [
      editor,
      isHeaderFooterEditMode,
      isPathInsideHeaderFooter,
      moveCursorToFirstBodyTextOrDeselect
    ]
  );

  const handleEditableDoubleClickCapture = useCallback(
    (event: React.MouseEvent) => {
      const el = event.target as HTMLElement | null;
      const isHF = el?.closest?.('[data-header-footer="true"]');
      if (!isHF) return;

      event.preventDefault();
      event.stopPropagation();
      setIsHeaderFooterEditMode(true);
      try {
        const r = ReactEditor.findEventRange(editor, event.nativeEvent);
        const safe = sanitizeRange(r, editor);
        if (safe) {
          Transforms.select(editor, safe);
        }
      } catch (e) {}
      ReactEditor.focus(editor as ReactEditor);
    },
    [editor]
  );
  const onKeyDown = useKeyboardShortcuts({
    editor,
    saveDocument: () => {
      void saveDocument('manual');
    },
    handlePrint,
    toggleMark,
    insertLink,
    insertComment,
    toggleCommentPanel,
    clearFormatting: () => {
      clearSelections();
      Transforms.unsetNodes(editor, 'bold' as any, {
        at: [],
        match: Text.isText,
        mode: 'all'
      });
      Transforms.unsetNodes(editor, 'italic' as any, {
        at: [],
        match: Text.isText,
        mode: 'all'
      });
      Transforms.unsetNodes(editor, 'underline' as any, {
        at: [],
        match: Text.isText,
        mode: 'all'
      });
    },
    setAlignment,
    setFontSize,
    currentFontSize: toolbarSync.fontSize || 12,
    indentList,
    outdentList
  });

  return (
    <div
      className="editor-layout"
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        overflow: 'hidden',
        minHeight: 0
      }}
    >
      <div
        className="editor-panel show-scrollbar"
        ref={editorPanelRef}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={handleEditorDrop}
        onMouseDownCapture={handleEditableMouseDownCapture}
        onDoubleClickCapture={handleEditableDoubleClickCapture}
        style={{
          flex: 1,
          minWidth: 0,
          overflowY: 'auto',
          position: 'relative',
          backgroundColor: '#f8f9fa',
          padding: '1px 0px 40px 0px',
          height: '100%'
        }}
      >
        <Editable
          renderElement={ElementRenderer}
          renderLeaf={LeafRenderer}
          onKeyDown={onKeyDown}
          decorate={decorate}
          style={{minHeight: '100%'}}
        />
        <FloatingCommentButton insertComment={insertComment} />
        <SelectionPopupMenu
          toggleMark={toggleMark}
          setFontSize={setFontSize}
          setFontFamily={setFontFamily}
          toggleColor={toggleColor}
          setAlignment={setAlignment}
          toggleBlock={toggleBlock}
          insertLink={insertLink}
          insertComment={insertComment}
          handleToggleList={handleToggleList}
          toolbarSync={toolbarSync}
          openColorPicker={openColorPicker}
        />
      </div>
      <CommentGroupOverlay />
    </div>
  );
};

export default EditorPage;
